import type { BookingChannel } from "@tdelivr/api-client/generated/tenant/organization-hierarchy.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { asDay, type BoardRow } from "./board.ts";

/**
 * What the channels board does to the channels TEN-05 returns, away from any rendering.
 *
 * Built to `17 WP16 Tenant Admin.dc.html`, page `channels`:
 *
 * ```
 * channels:['Channels','TEN-05','Where customers can book: app, website, WhatsApp, phone and partner APIs.',
 *   [['Channels','5','done','enabled'],['Paused','1','closed','by you'],
 *    ['Awaiting store review','1','waiting','iOS app'],['Errors today','0','done','']],
 *   ['Channel','Surface','Last booking'],
 *   [['Rider app (Android)','Play Store 4.12.0','2 min ago','done','live'],
 *    ['Rider app (iOS)','App Store 4.12.0','—','waiting','in store review'],
 *    ['Website','rides.citycab.example','5 min ago','done','live'],
 *    ['WhatsApp','+254 700 000 111','41 min ago','done','live'],
 *    ['Phone booking','Dispatch desk','—','closed','paused']],
 *   'info','Pausing a channel shows customers a clear message, never a broken page.']
 * ```
 *
 * ## The recorded blocker was right, and it was right about the thing that cost the most
 *
 * It read: _"Nothing on the platform models a booking channel. TEN-05 holds organisation units, and
 * a listing of those — which it also lacks — would be a list of different things, not this board
 * with columns missing."_
 *
 * **Both halves held under re-measurement.** No contract on this platform mentions a booking
 * channel: not a `channelId` on a ride request, not a surface on a tenant, not a client record
 * outside the realm. The only thing resembling one is the set of OAuth clients in Keycloak, which
 * is how an application authenticates rather than what a tenant offers.
 *
 * And the second half is the reason R-39's five listings stopped here: publishing
 * `GET /v1/tenants/{tenantId}/organization-units` would have made this page report as no longer
 * blocked while answering a page of organisations where the design draws app builds and phone
 * numbers. [ADR 0091](../../../docs/adr/0091-a-booking-channel-is-a-part-of-a-tenant-and-nothing-recorded-one.md)
 * adds the record.
 *
 * ## Two of three columns are published and the third is the whole of what remains
 *
 * | column         | source                                                                     |
 * | -------------- | --------------------------------------------------------------------------- |
 * | `Channel`      | `type`, through the design's own label map — never the stored enum value   |
 * | `Surface`      | `handle`, which is a number, a domain or an app ID depending on the type    |
 * | `Last booking` | **nothing.** No service links a booking to the channel it arrived through   |
 *
 * **`Last booking` is the honest remaining absence**, and it is a bigger one than it looks: making
 * it answerable means a channel reference on a ride request and on a delivery order — a field on
 * two other services' aggregates, written by whichever surface accepted the booking. That is a
 * platform change rather than a column, and it is recorded rather than attempted.
 *
 * **`Surface` is published and incomplete.** The design writes `Play Store 4.12.0` for the two app
 * rows: a store and a **version**. TEN-05 holds the app identifier and nothing holds a released
 * version — no build record, no release aggregate — so the cell draws the handle and the row's
 * detail panel says what the version would need.
 */

/** The most rows one request answers, which is this listing's own maximum. */
export const PER_FETCH = 200;

/** The scope the board spends. Not `organization:read` — ADR 0091 §2 argues the split. */
export const CHANNELS_READ_SCOPE = "booking-channel:read";
/** The scope the design's `Add channel` form would spend. */
export const CHANNELS_WRITE_SCOPE = "booking-channel:write";

/**
 * The design's own words for each stored type.
 *
 * TEN-05 publishes `rider_app_android`; the design writes `Rider app (Android)`. The map is here
 * rather than read from the service, because a label is this console's business and a value is the
 * service's — the same split the catalogue board records about MOB-01's own constant `lifecycle`.
 *
 * A type the service adds and this map does not know falls back to the stored value rather than to
 * a prettified one: title-casing `partner_api` into `Partner Api` would be this console inventing a
 * name for somebody else's record, which is the refusal the finance reports board makes about a
 * trigger key.
 */
export const TYPE_LABELS: Readonly<Record<string, string>> = {
  rider_app_android: "Rider app (Android)",
  rider_app_ios: "Rider app (iOS)",
  website: "Website",
  whatsapp: "WhatsApp",
  phone: "Phone booking",
  partner_api: "Partner API",
};

/** The design's `Channel` cell. */
export function channelCell(channel: BookingChannel): string {
  return TYPE_LABELS[channel.type] ?? channel.type;
}

/**
 * The design's two derivable badge words, and the third it draws that cannot be.
 *
 * `done · live` and `closed · paused` are the two states TEN-05 holds. **`waiting · in store
 * review` is not derivable at all**: it is a fact about an app store's review queue, which nothing
 * on this platform observes and no administrator could set truthfully — so a third status an
 * operator could choose would be a claim about a third party. The board says so under the grid.
 */
export function stateOf(channel: BookingChannel): {
  classKey: StateClassKey;
  label: string;
  title: string;
} {
  return channel.status === "live"
    ? {
        classKey: "done",
        label: "live",
        title:
          "TEN-05 publishes live. Customers can book through this channel; whether an app store has approved the build behind it is a different question nothing here answers.",
      }
    : {
        classKey: "closed",
        label: "paused",
        title:
          "TEN-05 publishes paused. The design's note says pausing shows customers a clear message rather than a broken page; what that message is belongs to the surface rather than to this record.",
      };
}

/** The states present in what came back, in board order, as the design's own `gridUI` does. */
export function statesPresent(channels: readonly BookingChannel[]): readonly string[] {
  return [...new Set(channels.map((channel) => stateOf(channel).label))];
}

/** Whether a channel belongs under the chosen state filter. `""` is unfiltered. */
export function matchesState(channel: BookingChannel, status: string): boolean {
  return status === "" || stateOf(channel).label === status;
}

/** Why the design's `Last booking` column is empty. */
export const LAST_BOOKING_ABSENT =
  "Nothing on this platform links a booking to the channel it arrived through. MOB-03's ride requests and the delivery orders beside them carry no channel reference, so answering this column means a field on two other services' aggregates written by whichever surface accepted the booking — a platform change rather than a column.";

/** One row of the channels board. */
export function channelRow(channel: BookingChannel): BoardRow {
  const isApp = channel.type === "rider_app_android" || channel.type === "rider_app_ios";
  return {
    id: channel.channelId,
    cells: [
      {
        /*
         * The design's own label, not the stored enum, and not the display name either. The two
         * differ: `label` is what a tenant typed — `WhatsApp Nairobi` is the form's own example —
         * and the first column is the **kind** of surface, which is what the design's rows read.
         * The tenant's own name for it is in the panel.
         */
        text: channelCell(channel),
        title: `TEN-05 publishes the type ${channel.type}. This tenant named this channel "${channel.label}", which is in the detail panel.`,
      },
      {
        text: channel.handle,
        mono: true,
        title: isApp
          ? `TEN-05 publishes the app identifier. The design writes a store and a version — "Play Store 4.12.0" — and nothing on this platform holds a released version: there is no build record and no release aggregate.`
          : `TEN-05 publishes the channel's number, domain or desk, which is one field for three kinds of thing because the design's own form is one input.`,
      },
      {
        /*
         * The design's `2 min ago`, `5 min ago`, `41 min ago` and two dashes. Nothing on this
         * platform links a booking to the channel it arrived through, so this is the one column
         * here with no source at all — and the design already draws it empty on two of its five
         * rows, which is the shape this cell takes on all of them.
         */
        text: "",
        absent: true,
        title: LAST_BOOKING_ABSENT,
      },
    ],
    state: stateOf(channel),
    detail: [
      { label: "Channel", value: channel.channelId, mono: true },
      // What the tenant called it, which the design's grid does not show and its form collects.
      { label: "Display name", value: channel.label },
      { label: "Type", value: channel.type, mono: true },
      { label: "Number, domain or app ID", value: channel.handle, mono: true },
      {
        label: "Services offered",
        /*
         * The design's chips. An empty list is a real state and not an absence: a channel offering
         * nothing is a channel through which nothing can be booked, which an administrator may have
         * meant, so it says so rather than drawing the dash that means "nothing publishes this".
         */
        value: channel.services.length === 0 ? "none" : [...channel.services].join(", "),
      },
      {
        label: "App store version",
        value: "—",
        absent: true,
      },
    ],
    history: [
      { label: "Added", value: asDay(channel.createdAt) },
      { label: "Last changed", value: asDay(channel.updatedAt) },
      { label: "Version", value: `v${String(channel.version)}` },
    ],
    glyph: "devices",
  };
}

/**
 * The panel's own absences, in words, because `DetailField` carries no title.
 *
 * Two lines on this panel are a dash and they mean different things, which is exactly the
 * distinction this list exists to keep: `Services offered` can be genuinely empty and says `none`,
 * while `App store version` is something nothing holds.
 */
export const DETAIL_NOTES: readonly { readonly label: string; readonly note: string }[] = [
  {
    label: "App store version",
    note: "Nothing on this platform holds a released app version — no build record and no release aggregate — so the design's `Play Store 4.12.0` draws the app identifier alone.",
  },
  {
    label: "Services offered",
    note: "The MOB-01 service codes bookable through this channel, as recorded. TEN-05 does not validate them against MOB-01: a foreign key across a service boundary is coupling this platform refuses, and a synchronous check would make channels unwritable whenever MOB-01 is down. So a code can be wrong here and nothing will say so.",
  },
];

/** The design's own closing note, kept word for word. */
export const BOARD_NOTE = "Pausing a channel shows customers a clear message, never a broken page.";

/** The sentence under the grid, carrying what a reader is looking at and cannot see. */
export const LISTING_NOTE =
  "Every way customers can reach this tenant, in channel-type order. Last booking is empty because nothing on this platform links a booking to the channel it arrived through. The design's third badge word, in store review, is not drawn either: whether an app store has approved a build is that store's to say, and a status an administrator could set by hand would be a claim about a third party. Its four tiles are not drawn — two are counts over this listing, one is the same store-review absence, and nothing counts errors per channel.";

/**
 * Why the design's `Add channel` form is drawn and not sent.
 *
 * **This one is a scope and not an absence**, which makes it the first refusal on this console with
 * a straightforward answer: ADR 0091 publishes `POST /v1/tenants/{tenantId}/booking-channels`
 * taking exactly the form's five fields, and this console does not ask for the write scope.
 *
 * It does not ask because `navigation.ts` declares what each page spends and this board was added
 * as a read. Granting the write is one line there and one grant on the realm client — and it is
 * deliberately not taken in the same change as drawing the board, so that a console gaining the
 * ability to open a booking surface is its own decision with its own record.
 */
export const ADD_CHANNEL_REFUSAL =
  "This board holds no write scope. TEN-05 does publish the command this form would send — it takes exactly these five fields — so unlike most refusals on this console, this one is a permission rather than a missing route. Granting booking-channel:write to this console is a decision with its own record.";
